import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { AttrSlugType } from '~src/interfaces/attributes.types';
import { AggregationValue, AggregationValueWithByCycle } from '~src/interfaces/heatmap.interface';
import { Scale } from '~src/interfaces/scale.interface';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { getAttributeBySlug } from '~src/modules/attributes/reducers/attributes.reducer';
import EmptyTile from '~src/modules/heatmap/components/table/tile/EmptyTile';
import RatingTile from '~src/modules/heatmap/components/table/tile/RatingTile';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { Uuid } from '~src/services/uuid';

interface OwnProps {
  aggregationValues?: Record<string, AggregationValueWithByCycle | AggregationValue>;
  globalValues?: ESQuestionOrTopic[];
  scales: { [key: string]: Scale };
  scaleId: string | null;
  segment: Segment;
  topicId?: string | null;
  selectedPeriod?: PeriodValues;
  filterSurvey?: string | null;
}

interface StateProps {
  anonymityMinAnswersAttr: number;
}

interface ESQuestionOrTopic {
  id: string;
  scale_version_id?: string;
}

type Props = OwnProps & StateProps;

const TilesRow: FC<Props> = (props) => {
  const { topicId, aggregationValues, globalValues, scales, scaleId, segment, selectedPeriod, filterSurvey } =
    props;

  const [t] = useTranslation();
  const buildLink = (questionOrTopicId: string) => {
    if (segment.type === SegmentType.Anonymous) {
      return;
    }
    return routes.reports.createQuestionsAnswersProfileLink(
      segment.type,
      segment.id,
      Uuid.fromHexString(topicId || questionOrTopicId),
      Uuid.fromHexStringOrUndefined(topicId ? questionOrTopicId : ''),
      selectedPeriod,
      filterSurvey,
    );
  };

  const getScale = useCallback(
    (valScaleId: string | undefined) => {
      let scale = null;
      if (valScaleId) {
        scale = scales[valScaleId];
      } else if (scaleId) {
        scale = scales[scaleId];
      }
      return scale;
    },
    [scales, scaleId],
  );

  return (
    <>
      {globalValues &&
        aggregationValues &&
        globalValues.map((qoT: ESQuestionOrTopic) => {
          const aggregationValue = aggregationValues?.[qoT.id];
          const ratingValue = aggregationValue?.average ?? aggregationValue?.top_chosen_option;
          const hasRatingValue = ratingValue === 0 || ratingValue;

          if (!hasRatingValue) {
            return <EmptyTile key={qoT.id} t={t} />;
          }

          const link = buildLink(qoT.id);
          const tile = (
            <RatingTile
              t={t}
              scale={getScale(qoT.scale_version_id)}
              answer={aggregationValue}
              zoomOnHover={Boolean(link)}
              key={aggregationValue.id}
            />
          );
          if (link) {
            return (
              <Link key={aggregationValue.id} to={link}>
                {tile}
              </Link>
            );
          }

          return tile;
        })}
    </>
  );
};

const mapStateToProps = (state: StoreI): StateProps => {
  let anonymityMinAnswersAttr = 0;

  const minAttr = getAttributeBySlug(state, AttrSlugType.AnonymityMinAnswers);
  const companyData = state.user.session.company;
  if (minAttr && companyData && minAttr.uuid in companyData.setting_attributes) {
    anonymityMinAnswersAttr = companyData.setting_attributes[minAttr.uuid];
  }

  return { anonymityMinAnswersAttr };
};

export default React.memo(connect(mapStateToProps)(TilesRow));
